import axios from 'axios'

const SCHOLAR_API_BASE = process.env.VUE_APP_SCHOLAR_API || '/api/scholar'

function getScholarAuthUser() {
  const authContext = localStorage.getItem('auth-context')
  if (!authContext) {
    return null
  }
  try {
    const users = JSON.parse(authContext)
    return users.find(item => item && (item.type === 'Person' || item.type === 'Child') && item.name) || null
  } catch (e) {
    return null
  }
}

function getScholarUserId() {
  const user = getScholarAuthUser()
  return user && user.name ? user.name : 'anonymous'
}

function getScholarUserType() {
  const user = getScholarAuthUser()
  return user && user.type ? user.type : 'anonymous'
}

function buildScholarHeaders() {
  const token = localStorage.getItem('token')
  const headers = {
    'X-User-Id': getScholarUserId(),
    'X-User-Type': getScholarUserType()
  }
  if (token) {
    headers.Authorization = token
  }
  return headers
}

function request(path, config) {
  return axios({
    url: `${SCHOLAR_API_BASE}${path}`,
    headers: buildScholarHeaders(),
    ...(config || {})
  }).then(response => response.data)
}

function parseDownloadFilename(contentDisposition) {
  if (!contentDisposition) {
    return ''
  }
  const encodedMatch = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i)
  if (encodedMatch && encodedMatch[1]) {
    try {
      return decodeURIComponent(encodedMatch[1].replace(/"/g, ''))
    } catch (e) {
      return encodedMatch[1].replace(/"/g, '')
    }
  }
  const plainMatch = contentDisposition.match(/filename="?([^";]+)"?/i)
  return plainMatch && plainMatch[1] ? plainMatch[1] : ''
}

export function searchScholar(params) {
  return request('/search', {
    method: 'get',
    params
  })
}

export function getScholarFacets(params) {
  return request('/facets', {
    method: 'get',
    params
  })
}

export function suggestScholar(q, entity) {
  return request('/suggest', {
    method: 'get',
    params: {
      q,
      entity: entity || 'works'
    }
  })
}

export function resolveScholarIdentifier(q) {
  return request('/resolve', {
    method: 'get',
    params: {
      q
    }
  })
}

export function listScholarSearchHistory(limit) {
  return request('/search-history', {
    method: 'get',
    params: {
      limit: limit || 12
    }
  })
}

export function getScholarWork(provider, id) {
  return request(`/works/${encodeURIComponent(provider)}/${encodeURIComponent(id)}`, {
    method: 'get'
  })
}

export function bulkImportScholarWorks(text, identifiers) {
  return request('/works/bulk', {
    method: 'post',
    data: {
      text: text || '',
      identifiers: identifiers || []
    }
  })
}

export function downloadScholarWorkPdf(provider, id) {
  return axios({
    url: `${SCHOLAR_API_BASE}/works/${encodeURIComponent(provider)}/${encodeURIComponent(id)}/pdf`,
    method: 'get',
    responseType: 'blob',
    headers: buildScholarHeaders()
  }).then(response => ({
    blob: response.data,
    filename: parseDownloadFilename(response.headers['content-disposition'])
  })).catch(async error => {
    const data = error && error.response && error.response.data
    if (data instanceof Blob) {
      try {
        const text = await data.text()
        error.response.data = JSON.parse(text)
      } catch (e) {
        error.response.data = null
      }
    }
    throw error
  })
}

export function getScholarEntity(provider, entityType, id) {
  return request(`/entities/${encodeURIComponent(provider)}/${encodeURIComponent(entityType)}/${encodeURIComponent(id)}`, {
    method: 'get'
  })
}

export function getScholarWorkRelations(provider, id, size) {
  return request(`/works/${encodeURIComponent(provider)}/${encodeURIComponent(id)}/relations`, {
    method: 'get',
    params: {
      size: size || 5
    }
  })
}

export function saveScholarWork(work, tags, note) {
  return request('/saved-works', {
    method: 'post',
    data: {
      work,
      tags: tags || [],
      note: note || ''
    }
  })
}

export function listSavedScholarWorks(params) {
  return request('/saved-works', {
    method: 'get',
    params
  })
}

export function pageSavedScholarWorks(params) {
  return request('/saved-works/page', {
    method: 'get',
    params
  })
}

export function deleteSavedScholarWork(workKey) {
  return request(`/saved-works/${encodeURIComponent(workKey)}`, {
    method: 'delete'
  })
}

export function saveScholarSearch(name, query, note) {
  return request('/saved-searches', {
    method: 'post',
    data: {
      name: name || '',
      note: note || '',
      query: query || {}
    }
  })
}

export function listSavedScholarSearches() {
  return request('/saved-searches', {
    method: 'get'
  })
}

export function deleteSavedScholarSearch(searchKey) {
  return request(`/saved-searches/${encodeURIComponent(searchKey)}`, {
    method: 'delete'
  })
}
